import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformSDKBytesToRESTBytes } from '@wix/sdk-runtime/transformations/bytes';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformSDKFieldMaskToRESTFieldMask } from '@wix/sdk-runtime/transformations/field-mask';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixEnterpriseWebFiveV1IconServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'api._api_base_domain_': [
      {
        srcPath: '/media-manager',
        destPath: '',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/_api/web5-media-manager',
        destPath: '',
      },
    ],
    'bo._base_domain_': [
      {
        srcPath: '/_api/media-manager',
        destPath: '',
      },
    ],
    'wixbo.ai': [
      {
        srcPath: '/_api/media-manager',
        destPath: '',
      },
    ],
    'wix-bo.com': [
      {
        srcPath: '/_api/media-manager',
        destPath: '',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_media-manager_icons';

/**
 * Creates an Icon. On PUBLIC calls the icon's `msid` is set from the caller's
 * signed context; the request body's msid (if any) is ignored. On INTERNAL calls
 * the request may pass `private_msid` to scope the icon to a metasite, otherwise
 * the icon is created as global.
 */
export function createIcon(payload: object): RequestOptionsFactory<any> {
  function __createIcon({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKBytesToRESTBytes,
        paths: [{ path: 'content' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [{ path: 'icon.createdDate' }, { path: 'icon.updatedDate' }],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.enterprise.web_five.v1.icon',
      method: 'POST' as any,
      methodFqn: 'wix.enterprise.web_five.v1.IconService.CreateIcon',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEnterpriseWebFiveV1IconServiceUrl({
        protoPath: '/v1/icons',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [{ path: 'icon.createdDate' }, { path: 'icon.updatedDate' }],
          },
        ]),
    };

    return metadata;
  }

  return __createIcon;
}

/**
 * Retrieves an Icon by ID. Enforces tenancy: returns the icon only if it is
 * global or owned by the caller's metasite. Returns NOT_FOUND otherwise.
 */
export function getIcon(payload: object): RequestOptionsFactory<any> {
  function __getIcon({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.enterprise.web_five.v1.icon',
      method: 'GET' as any,
      methodFqn: 'wix.enterprise.web_five.v1.IconService.GetIcon',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEnterpriseWebFiveV1IconServiceUrl({
        protoPath: '/v1/icons/{iconId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [{ path: 'icon.createdDate' }, { path: 'icon.updatedDate' }],
          },
        ]),
    };

    return metadata;
  }

  return __getIcon;
}

/**
 * Updates metadata fields of an Icon (name, keywords, collection, category, type,
 * weight, description). The binary is immutable post-create; to replace bytes,
 * delete + create. Enforces tenancy on writes (PUBLIC: only on caller-owned
 * private icons; INTERNAL: any).
 */
export function updateIconMetadata(
  payload: object
): RequestOptionsFactory<any> {
  function __updateIconMetadata({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'fieldMask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [{ path: 'icon.createdDate' }, { path: 'icon.updatedDate' }],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.enterprise.web_five.v1.icon',
      method: 'PATCH' as any,
      methodFqn: 'wix.enterprise.web_five.v1.IconService.UpdateIconMetadata',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEnterpriseWebFiveV1IconServiceUrl({
        protoPath: '/v1/icons/{icon.id}',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [{ path: 'icon.createdDate' }, { path: 'icon.updatedDate' }],
          },
        ]),
    };

    return metadata;
  }

  return __updateIconMetadata;
}

/**
 * Deletes an Icon by ID. Removes the Vespa document immediately; the Wix Media
 * binary is left in place and cleaned up by a periodic orphan-collector job.
 * Tenancy enforced (PUBLIC cannot delete globals or other clients' private icons).
 */
export function deleteIcon(payload: object): RequestOptionsFactory<any> {
  function __deleteIcon({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.enterprise.web_five.v1.icon',
      method: 'DELETE' as any,
      methodFqn: 'wix.enterprise.web_five.v1.IconService.DeleteIcon',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEnterpriseWebFiveV1IconServiceUrl({
        protoPath: '/v1/icons/{iconId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __deleteIcon;
}

/**
 * Hybrid (BM25 + vector) search over icons. Always applies a tenancy filter:
 * returns global icons and the caller's own private icons by default, narrowable
 * via `filter.tenant_scope`. `filter.exclude_ids` drops specific icons from
 * results (useful for clients who don't want certain global icons to appear).
 */
export function searchIcons(payload: object): RequestOptionsFactory<any> {
  function __searchIcons({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'filter.updatedAfter' },
          { path: 'filter.updatedBefore' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.enterprise.web_five.v1.icon',
      method: 'POST' as any,
      methodFqn: 'wix.enterprise.web_five.v1.IconService.SearchIcons',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEnterpriseWebFiveV1IconServiceUrl({
        protoPath: '/v1/icons/search',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [{ path: 'icons.relevance' }],
          },
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'icons.icon.createdDate' },
              { path: 'icons.icon.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __searchIcons;
}

/**
 * Bulk-assign / unassign platform Tags on a list of icons (AIP-7025).
 * A tag that appears in both `assign_tags` and `unassign_tags` is assigned.
 */
export function bulkUpdateIconTags(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkUpdateIconTags({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.enterprise.web_five.v1.icon',
      method: 'POST' as any,
      methodFqn: 'wix.enterprise.web_five.v1.IconService.BulkUpdateIconTags',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEnterpriseWebFiveV1IconServiceUrl({
        protoPath: '/v1/bulk/icons/update-tags',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __bulkUpdateIconTags;
}

/**
 * Bulk-assign / unassign platform Tags on icons matching a filter. Async — returns
 * a job id. An empty filter targets all icons.
 */
export function bulkUpdateIconTagsByFilter(
  payload: object
): RequestOptionsFactory<any> {
  function __bulkUpdateIconTagsByFilter({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.enterprise.web_five.v1.icon',
      method: 'POST' as any,
      methodFqn:
        'wix.enterprise.web_five.v1.IconService.BulkUpdateIconTagsByFilter',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEnterpriseWebFiveV1IconServiceUrl({
        protoPath: '/v1/bulk/icons/update-tags-by-filter',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __bulkUpdateIconTagsByFilter;
}

/**
 * INTERNAL one-off bulk seed for the initial 10k global catalog. Each item carries
 * either inline bytes or an external URL (which the service imports via Wix Media's
 * files/import). Capped at 100 items per request to stay under proto size limits;
 * callers paginate.
 */
export function bulkSeedIcons(payload: object): RequestOptionsFactory<any> {
  function __bulkSeedIcons({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKBytesToRESTBytes,
        paths: [{ path: 'items.content' }],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.enterprise.web_five.v1.icon',
      method: 'POST' as any,
      methodFqn: 'wix.enterprise.web_five.v1.IconService.BulkSeedIcons',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEnterpriseWebFiveV1IconServiceUrl({
        protoPath: '/v1/internal/icons/bulk-seed',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'results.icon.createdDate' },
              { path: 'results.icon.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __bulkSeedIcons;
}
